import type { MessageData, PaginatedResponse, SuccessResponse } from "@shared/types";
import apiClient from "@/app/api/client";
import { dailyAttendanceEndpoints, replaceParams, reportsEndpoints } from "@/app/api/endpoints";
import type {
	ListByStaffDailyAttendanceQuery,
	PaginatedStaffDailyAttendanceResponse,
} from "@/app/api/types";
import type {
	CreateDailyAttendanceRequest,
	DailyAttendance,
	ListDailyAttendanceParams,
	UpdateDailyAttendanceRequest,
} from "../types";

export interface AttendanceReportParams {
	startDate: string;
	endDate: string;
	organizationId?: string;
	staffId?: string;
	brigadeId?: string;
	groupBy?: "day" | "week" | "month" | "org" | "staff";
	format?: "json" | "csv" | "xlsx";
}

export const dailyAttendanceService = {
	async list(params: ListDailyAttendanceParams): Promise<PaginatedResponse<DailyAttendance>> {
		const response = await apiClient.get<PaginatedResponse<DailyAttendance>>(
			dailyAttendanceEndpoints.list,
			{ params }
		);
		return response.data;
	},

	async listByStaff(
		params: ListByStaffDailyAttendanceQuery
	): Promise<PaginatedStaffDailyAttendanceResponse["data"]> {
		const response = await apiClient.get<PaginatedStaffDailyAttendanceResponse>(
			dailyAttendanceEndpoints.listByStaff,
			{ params }
		);
		return response.data.data;
	},

	async getById(id: string): Promise<DailyAttendance> {
		const url = replaceParams(dailyAttendanceEndpoints.getById, { id });
		const response = await apiClient.get<SuccessResponse<DailyAttendance>>(url);
		return response.data.data;
	},

	async create(data: CreateDailyAttendanceRequest): Promise<DailyAttendance> {
		const response = await apiClient.post<SuccessResponse<DailyAttendance>>(
			dailyAttendanceEndpoints.create,
			data
		);
		return response.data.data;
	},

	async update(id: string, data: UpdateDailyAttendanceRequest): Promise<DailyAttendance> {
		const url = replaceParams(dailyAttendanceEndpoints.update, { id });
		const response = await apiClient.patch<SuccessResponse<DailyAttendance>>(url, data);
		return response.data.data;
	},

	async delete(id: string): Promise<void> {
		const url = replaceParams(dailyAttendanceEndpoints.delete, { id });
		await apiClient.delete<SuccessResponse<MessageData>>(url);
	},

	async exportAttendanceReport(params: AttendanceReportParams): Promise<Blob> {
		const response = await apiClient.get(reportsEndpoints.attendance, {
			params,
			responseType: "blob",
		});
		return response.data as Blob;
	},
};
